import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EditorActions = ({ artist, onSaveNote, onApproveContent, onRequestChanges }) => {
  const [note, setNote] = useState('');
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);

  const handleSaveNote = () => {
    if (note?.trim()) {
      onSaveNote(note);
      setNote('');
      setIsNoteExpanded(false);
    }
  };

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => 
      prev?.includes(section) 
        ? prev?.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const sections = ['Profile', 'Promotion', 'About', 'Calendar', 'Work', 'Shop'];

  return (
    <div className="bg-surface border border-border rounded-lg p-6 elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Editor Actions</h2>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full"></div>
          <span className="text-sm text-text-secondary">Online</span>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <Button
          variant="default"
          size="sm"
          iconName="CheckCircle"
          iconPosition="left"
          onClick={() => onApproveContent(selectedSections)}
          disabled={selectedSections?.length === 0}
          className="justify-start"
        >
          Approve Selected ({selectedSections?.length})
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          iconName="AlertCircle"
          iconPosition="left"
          onClick={() => onRequestChanges(selectedSections)}
          disabled={selectedSections?.length === 0}
          className="justify-start"
        >
          Request Changes
        </Button>
      </div>
      {/* Section Selection */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-text-primary mb-3">Select Sections for Action</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {sections?.map((section) => (
            <button
              key={section}
              onClick={() => handleSectionToggle(section)}
              className={`p-2 text-sm rounded-lg border transition-smooth ${
                selectedSections?.includes(section)
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-muted text-text-primary border-border hover:bg-accent/10'
              }`}
            >
              {section}
            </button>
          ))}
        </div>
      </div>
      {/* Notes Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-text-primary">Editor Notes</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName={isNoteExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsNoteExpanded(!isNoteExpanded)}
          >
            {isNoteExpanded ? 'Collapse' : 'Add Note'}
          </Button>
        </div>

        {isNoteExpanded && (
          <div className="space-y-3 animate-fade-in">
            <Input
              type="text"
              placeholder="Add a note for the artist..."
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              className="w-full"
            />
            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                onClick={handleSaveNote}
                disabled={!note?.trim()}
              >
                Save Note
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNote('');
                  setIsNoteExpanded(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Recent Notes */}
        <div className="mt-4 space-y-2">
          {artist?.recentNotes?.map((noteItem, index) => (
            <div key={index} className="p-3 bg-muted rounded-lg">
              <div className="flex items-start justify-between">
                <p className="text-sm text-text-primary">{noteItem?.content}</p>
                <span className="text-xs text-text-secondary whitespace-nowrap ml-2">
                  {noteItem?.timestamp}
                </span>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <span className="text-xs text-accent">{noteItem?.editor}</span>
                {noteItem?.sections && (
                  <span className="text-xs text-text-secondary">
                    • {noteItem?.sections?.join(', ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Communication Tools */}
      <div>
        <h3 className="text-sm font-medium text-text-primary mb-3">Communication</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="Mail"
            iconPosition="left"
            className="justify-start"
          >
            Send Email
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="MessageSquare"
            iconPosition="left"
            className="justify-start"
          >
            Internal Message
          </Button>
        </div>
      </div>
      {/* Status Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Last Editor Action:</span>
          <span className="text-text-primary">{artist?.lastEditorAction}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-text-secondary">Content Status:</span>
          <span className={`px-2 py-1 rounded text-xs ${
            artist?.contentStatus === 'approved' ?'bg-success/10 text-success' 
              : artist?.contentStatus === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
          }`}>
            {artist?.contentStatus}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EditorActions;